import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import api from './services/api';
import AdminLogin from './components/AdminLogin';
import Dashboard from './pages/Dashboard';
import Attractions from './pages/Attractions';
import EventManagement from './pages/EventManagement';
import Favorites from './pages/Favorites';
import Notifications from './pages/Notifications';
import Chat from './pages/Chat';
import BannerManagement from './pages/BannerManagement';
import HomeCardManagement from './pages/HomeCardManagement';
import FeedbackManager from './pages/FeedbackManager';
import QRCodeGenerator from './pages/QRCodeGenerator';
import UserProfile from './pages/UserProfile';
import UserPreferences from './pages/UserPreferences';
import AdminUserManagement from './pages/AdminUserManagement';
import ActivityLog from './pages/ActivityLog';
import MigrationProgress from './pages/MigrationProgress';
import NotificationManager from './pages/NotificationManager';
import ItineraryDashboard from './pages/ItineraryDashboard';
import ItineraryHistory from './pages/ItineraryHistory';
import ItineraryDetail from './pages/ItineraryDetail';
import ItineraryPresets from './pages/ItineraryPresets';
import ItineraryPresetDetail from './pages/ItineraryPresetDetail';
import ItineraryTesting from './pages/ItineraryTesting';
import UserItineraries from './pages/UserItineraries';
import QRAnalytics from './pages/QRAnalytics';
import StampbookAnalytics from './pages/StampbookAnalytics';
import StampbookConfiguration from './pages/StampbookConfiguration';
import FAQManager from './pages/FAQManager';
import './App.css';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontSize: '18px',
        color: '#667eea'
      }}>
        🌍 Loading Travel Futura...
      </div>
    );
  }

  return isAuthenticated() ? children : <Navigate to="/login" replace />;
};

// App content component (needs to be inside AuthProvider)
const AppContent = () => {
  const { isAuthenticated, loading, login } = useAuth();

  // Set document title from tenant config (e.g. "Imatra CMS")
  useEffect(() => {
    const setTitle = async () => {
      try {
        const response = await api.get('/api/v1/config');
        if (response.data?.success) {
          const name = response.data.data.destinationName || response.data.data.tenantName;
          if (name) document.title = `${name} CMS`;
        }
      } catch {
        // fallback title stays as "Travel Futura Admin" from index.html
      }
    };
    setTitle();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontSize: '18px',
        color: '#667eea'
      }}>
        🌍 Loading Travel Futura...
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated() ?
                <Navigate to="/" replace /> :
                <AdminLogin onLogin={login} />
            }
          />
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/attractions" element={
            <ProtectedRoute>
              <Attractions />
            </ProtectedRoute>
          } />
          
          <Route path="/events" element={
            <ProtectedRoute>
              <EventManagement />
            </ProtectedRoute>
          } />
          
          <Route path="/favorites" element={
            <ProtectedRoute>
              <Favorites />
            </ProtectedRoute>
          } />
          
          <Route path="/notifications" element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          } />
          
          <Route path="/chat" element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          } />
          
          <Route path="/banners" element={
            <ProtectedRoute>
              <BannerManagement />
            </ProtectedRoute>
          } />
          
          <Route path="/home-cards" element={
            <ProtectedRoute>
              <HomeCardManagement />
            </ProtectedRoute>
          } />
          
          <Route path="/feedback-manager" element={
            <ProtectedRoute>
              <FeedbackManager />
            </ProtectedRoute>
          } />
          
          <Route path="/qr-generator" element={
            <ProtectedRoute>
              <QRCodeGenerator />
            </ProtectedRoute>
          } />

          <Route path="/qr-analytics" element={
            <ProtectedRoute>
              <QRAnalytics />
            </ProtectedRoute>
          } />

          <Route path="/stampbook-analytics" element={
            <ProtectedRoute>
              <StampbookAnalytics />
            </ProtectedRoute>
          } />

          <Route path="/stampbook-configuration" element={
            <ProtectedRoute>
              <StampbookConfiguration />
            </ProtectedRoute>
          } />

          <Route path="/user/profile" element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          } />
          
          <Route path="/user/preferences" element={
            <ProtectedRoute>
              <UserPreferences />
            </ProtectedRoute>
          } />

          <Route path="/admin/users" element={
            <ProtectedRoute>
              <AdminUserManagement />
            </ProtectedRoute>
          } />

          <Route path="/faq-manager" element={
            <ProtectedRoute>
              <FAQManager />
            </ProtectedRoute>
          } />

          <Route path="/activity-log" element={
            <ProtectedRoute>
              <ActivityLog />
            </ProtectedRoute>
          } />

          <Route path="/migration-progress" element={
            <ProtectedRoute>
              <MigrationProgress />
            </ProtectedRoute>
          } />

          <Route path="/notification-manager" element={
            <ProtectedRoute>
              <NotificationManager />
            </ProtectedRoute>
          } />

          {/* Itinerary Planner routes */}
          <Route path="/itinerary/dashboard" element={
            <ProtectedRoute><ItineraryDashboard /></ProtectedRoute>
          } />
          <Route path="/itinerary/history" element={
            <ProtectedRoute><ItineraryHistory /></ProtectedRoute>
          } />
          <Route path="/itinerary/history/:id" element={
            <ProtectedRoute><ItineraryDetail /></ProtectedRoute>
          } />
          <Route path="/itinerary/presets" element={
            <ProtectedRoute><ItineraryPresets /></ProtectedRoute>
          } />
          <Route path="/itinerary/presets/:id" element={
            <ProtectedRoute><ItineraryPresetDetail /></ProtectedRoute>
          } />
          <Route path="/itinerary/testing" element={
            <ProtectedRoute><ItineraryTesting /></ProtectedRoute>
          } />
          <Route path="/itinerary/users/:userId" element={
            <ProtectedRoute><UserItineraries /></ProtectedRoute>
          } />

          {/* Catch all route - redirect to login if not authenticated, dashboard if authenticated */}
          <Route path="*" element={
            isAuthenticated() ? 
              <Navigate to="/" replace /> : 
              <Navigate to="/login" replace />
          } />
        </Routes>
      </div>
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
